import { Box, CircularProgress } from '@mui/material';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import { t } from '@lingui/core/macro';

/**
 * A busy indicator that respects the motion preference.
 *
 * The indeterminate spinner is a continuous rotation, and for a chat that can
 * sit there for the better part of a minute while the model thinks, that is a
 * long stretch of unrequested movement. Where motion is reduced it is drawn as
 * a fixed 3/4 arc instead: still unmistakably "working", but it stops moving.
 *
 * The arc is never hidden outright -- a spinner with no motion reads as a stuck
 * screen, which is worse than a moving one.
 */
export function ChatSpinner({ size = 16, sx }: { size?: number; sx?: object }) {
  const reducedMotion = usePrefersReducedMotion();

  if (reducedMotion) {
    return (
      <Box
        role="progressbar"
        aria-label={t`Working`}
        sx={{
          width: size,
          height: size,
          flex: '0 0 auto',
          borderRadius: '50%',
          // A static arc, drawn rather than rotated.
          border: '2px solid currentColor',
          borderTopColor: 'transparent',
          borderRightColor: 'transparent',
          opacity: 0.6,
          ...sx,
        }}
      />
    );
  }

  return (
    <>
      <Box
        role="progressbar"
        aria-label={t`Working`}
        sx={{ display: 'inline-flex', ...sx }}
      >
        {/* MUI emits role="progressbar" only for the determinate variant, so an
            indeterminate spinner was invisible to assistive technology. */}
        <CircularProgress size={size} />
      </Box>
    </>
  );
}
